import * as React from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Box from '@mui/material/Box';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import IconButton from '@mui/material/IconButton';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';
import { Typography } from '@mui/material';
import { Conversation } from 'common/types';
import { useMixpanel } from 'utils/hooks/useMixPanel';

type Props = {
  requestSendMediaMessage: (
    mediaBlob: Blob,
    fileName?: string,
    onSuccess?: VoidFunction,
    onFinally?: VoidFunction
  ) => Promise<void>;
  selectedConversation: Conversation;
  selectedDevice: string;
};

export default function SendMedia(props: Props) {
  const { requestSendMediaMessage, selectedConversation, selectedDevice } =
    props;
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event?) => {
    if (anchorRef?.current && event?.target && anchorRef?.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const { chatInteraction, contentSent } = useMixpanel();

  const handleFileSelection = (event) => {
    const file = event.target.files[0];
    chatInteraction(selectedDevice, 'message-sent', {
      conversationId: selectedConversation?.id
    });
    let contentType: 'audio' | 'photo-or-video' | 'document' = 'document';

    if (file.type.startsWith('audio/')) {
      contentType = 'audio';
    } else if (
      file.type.startsWith('image/') ||
      file.type.startsWith('video/')
    ) {
      contentType = 'photo-or-video';
    }
    contentSent(selectedDevice, contentType);

    if (file) {
      uploadMedia(file);
    }
  };

  const openFilePicker = (accept) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      fileInputRef.current.click();
    }
  };

  const uploadMedia = (file) => {
    const blob = new Blob([file], { type: file.type });
    requestSendMediaMessage(blob, file.name, handleClose);
  };

  return (
    <Box alignContent='center'>
      <IconButton
        ref={anchorRef}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
      >
        <AttachFileIcon fontSize='medium' />
      </IconButton>
      <Popper
        sx={{ zIndex: 5 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open}>
                  <MenuItem onClick={() => openFilePicker('image/*,video/*')}>
                    <Typography alignContent='center' display='flex' gap={1}>
                      <ImageOutlinedIcon color='primary' /> Foto ou Vídeo
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => openFilePicker('*/*')}>
                    <Typography alignContent='center' display='flex' gap={1}>
                      <InsertDriveFileOutlinedIcon color='primary' /> Documento
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => openFilePicker('audio/*')}>
                    <Typography alignContent='center' display='flex' gap={1}>
                      <HeadphonesOutlinedIcon color='primary' /> Áudio
                    </Typography>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelection}
      />
    </Box>
  );
}
